import { useEffect, useState } from 'react'
import { useApp } from '../context/AppContext'
import { AnimatePresence, motion } from 'framer-motion'
import { sileo } from 'sileo'

// Components
import SettingsSection from '../components/Settings/SettingsSection'
import TicketCategoryAdd from '../components/ticketCategory/TicketCategoryAdd'
import TicketCategoryEdit from '../components/ticketCategory/TicketCategoryEdit'
import TicketCategoryTable from '../components/ticketCategory/TicketCategoryTable'
import OffCanvas from '../components/Shared/OffCanvas'
import Modal from '../components/Shared/Modal'

// Hooks
import useOffCanvas from '../hooks/useOffCanvas'
import useModal from '../hooks/useModal'

const TicketCategories = () => {
  const { supabase } = useApp()
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(false)

  const { isOpen, config, close } = useOffCanvas()
  const {
    openModal,
    isOpen: isModalOpen,
    modalConfig,
    closeModal,
    isLargeModal,
  } = useModal()

  // ==========================================
  // GET
  // ==========================================
  const getCategories = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .schema('Tickets')
        .from('category')
        .select('*')
        .order('name', { ascending: true })

      if (error) throw error
      setCategories(data || [])
    } catch (error) {
      console.error('Error fetching categories:', error)
      sileo.error({
        title: 'Error de base de datos',
        description: 'No se pudieron cargar las categorías.',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getCategories()
  }, [])

  // ==========================================
  // CREATE
  // ==========================================
  const handleAdded = () => {
    closeModal()
    getCategories()
  }

  // ==========================================
  // UPDATE
  // ==========================================
  const handleUpdateCategory = async (id, name) => {
    try {
      const { error } = await supabase
        .schema('Tickets')
        .from('category')
        .update({ name })
        .eq('id', id)

      if (error) throw error

      sileo.success({
        title: 'Categoría actualizada',
        description: 'El nombre se actualizó correctamente.',
      })

      closeModal()
      getCategories()
    } catch (error) {
      console.error('Error updating category:', error)
      sileo.error({
        title: 'Error',
        description: 'No se pudo actualizar la categoría.',
      })
    }
  }

  // ==========================================
  // OPEN EDIT MODAL
  // ==========================================
  const handleEdit = (category) => {
    openModal(
      'Editar categoría',
      <TicketCategoryEdit category={category} onSave={handleUpdateCategory} />
    )
  }

  return (
    <div className="tc-module">
      {/* OVERLAYS */}
      <AnimatePresence mode="wait">
        {isOpen && (
          <motion.div
            key="offcanvas-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <OffCanvas isOpen={isOpen} onClose={close} title={config.title}>
              <motion.div
                initial={{ x: 60, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 60, opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              >
                {config.component}
              </motion.div>
            </OffCanvas>
          </motion.div>
        )}

        {isModalOpen && (
          <motion.div
            key="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Modal
              isOpen={isModalOpen}
              onClose={closeModal}
              title={modalConfig.title}
              isLarge={isLargeModal}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.9, y: 20, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {modalConfig.component}
              </motion.div>
            </Modal>
          </motion.div>
        )}
      </AnimatePresence>

      <SettingsSection
        title="Categorías"
        icon="fa-tags"
        description="Clasifica las solicitudes por área o tipo."
        actions={
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() =>
              openModal(
                'Nueva categoría',
                <TicketCategoryAdd onAdded={handleAdded} />
              )
            }
          >
            <i className="fa-solid fa-plus me-2" />
            Nueva categoría
          </button>
        }
      >
        {loading ? (
          <div className="tt" style={{ flex: 'none' }}>
            <div className="tt-empty">
              <span className="spinner-border spinner-border-sm text-primary me-2" />
              Cargando categorías...
            </div>
          </div>
        ) : (
          <TicketCategoryTable
            categories={categories}
            onRefresh={getCategories}
            onEdit={handleEdit}
          />
        )}
      </SettingsSection>
    </div>
  )
}

export default TicketCategories
